import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CrmActivity } from '../../interfaces/opportunity';

/**
 * Shared CRM activity timeline used by the opportunity and submission detail
 * pages, so both render identically. Self-contained from CrmActivity[]; the
 * host owns the empty/loading state (it knows isLoading) and the actual
 * update/delete API calls — this component only emits {id, body} / id and
 * lets the host reconcile its local array. Editing is in-place overwrite (no
 * "edited" stamp / history) — the simplest option given a `note` row already
 * carries `occurred_at` for when the thing itself happened, and the app has
 * no versioning story elsewhere for user-authored CRM data.
 */
@Component({
  selector: 'bst-crm-timeline',
  templateUrl: './crm-timeline.component.html',
  styleUrls: ['./crm-timeline.component.scss'],
  standalone: false,
})
export class CrmTimelineComponent {
  @Input() activities: CrmActivity[] = [];
  /** Whether the current user may edit/delete user-loggable rows (mirrors the host's canCreate/canWrite gate). */
  @Input() canEdit = false;
  @Output() edit = new EventEmitter<{ id: number; body: string; occurred_at?: string }>();
  @Output() delete = new EventEmitter<number>();

  editingId: number | null = null;
  editBody = '';
  editDate: Date | null = null;
  editTime: string | null = null;
  /** Only true once the user actually touches the date/time controls — lets
   * a body-only edit omit occurred_at entirely so the backend's isset-based
   * preserve keeps the exact stored value (edit-date/time round-trips
   * through this component at minute precision, so re-sending it on every
   * save would silently truncate any second-level precision on the row). */
  editDateTouched = false;

  /** Not a fixed field: a page left open across midnight must still allow today. */
  get today(): Date { return new Date(); }

  /** "status_change" -> "status change" (CSS capitalises each word for display). */
  typeLabel(type: string): string {
    return (type || '').replace(/_/g, ' ');
  }

  /** Auto-emitted rows (status change / system) get the quieter accent and are never editable. */
  isAuto(type: string): boolean {
    return type === 'status_change' || type === 'system';
  }

  who(a: CrmActivity): string {
    return a.user ? `${a.user.name_first} ${a.user.name_last}`.trim() : '';
  }

  startEdit(a: CrmActivity): void {
    this.editingId = a.id;
    this.editBody = a.body;
    const occurred = a.occurred_at ? new Date(a.occurred_at) : null;
    this.editDate = occurred;
    this.editTime = occurred ? `${String(occurred.getHours()).padStart(2, '0')}:${String(occurred.getMinutes()).padStart(2, '0')}` : null;
    this.editDateTouched = false;
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editBody = '';
    this.editDate = null;
    this.editTime = null;
    this.editDateTouched = false;
  }

  saveEdit(): void {
    const body = this.editBody.trim();
    if (!body || this.editingId == null) { return; }
    this.edit.emit({ id: this.editingId, body, occurred_at: this.editDateTouched ? this.toIsoOrUndefined() : undefined });
    this.editingId = null;
    this.editBody = '';
    this.editDate = null;
    this.editTime = null;
    this.editDateTouched = false;
  }

  /** Same date+time -> ISO combination as the composer (crm-activity-log.component.ts). */
  private toIsoOrUndefined(): string | undefined {
    if (!this.editDate) { return undefined; }
    const [hours, minutes] = (this.editTime || '00:00').split(':').map(Number);
    const d = new Date(this.editDate);
    d.setHours(hours || 0, minutes || 0, 0, 0);
    // Clamp to now: the server rejects a future occurred_at outright, and a
    // rejected save currently surfaces no error to the user (host catch just
    // logs) — clamping here is cheap insurance against a silently lost edit.
    const now = Date.now();
    return d.getTime() > now ? new Date(now).toISOString() : d.toISOString();
  }

  remove(a: CrmActivity): void {
    this.delete.emit(a.id);
  }
}
